import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, Info, Loader2, RotateCcw, Save, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/hooks/use-permissions';
import { useBusinessUnit } from '@/hooks';
import {
	CapabilityDocConflict,
	CapabilityDocInvalid,
	processingCapabilityService,
	type CapabilityDoc,
	type CapabilityDocIssue,
	type ProcessingCapability
} from '@/services/processing-capability.service';

/**
 * Ticket processing capabilities.
 *
 * The document that says what Kim can do to a ticket in code — tag it, close it, assign
 * it — written in markdown by the people who run support rather than shipped in a release.
 * The SOP → workflow pipeline reads it on every generation: a step it recognises here is
 * offered as deterministic processing instead of becoming a workflow node.
 *
 * The page is deliberately two panes. Markdown alone hides whether a capability actually
 * parsed, and a form alone cannot hold the prose that makes the document worth reading. So:
 * you edit the document on the left, and the right shows exactly what the pipeline will
 * see, with anything wrong called out against its line.
 */

/** Debounce for the live parse — long enough not to fire mid-word. */
const PREVIEW_DEBOUNCE_MS = 500;

function errorsOf(issues: CapabilityDocIssue[]): CapabilityDocIssue[] {
	return issues.filter((issue) => issue.severity === 'error');
}

function warningsOf(issues: CapabilityDocIssue[]): CapabilityDocIssue[] {
	return issues.filter((issue) => issue.severity === 'warning');
}

/** One parsed capability, as the pipeline will see it. */
function CapabilityCard({ capability }: { capability: ProcessingCapability }) {
	return (
		<li className='rounded-xl border border-gray-300 p-3'>
			<div className='flex flex-wrap items-center gap-2'>
				<span className='text-[13px] font-medium text-ink'>{capability.label}</span>
				<Badge
					variant='outline'
					className={cn(
						'rounded-full border-0 text-[10px] font-medium',
						capability.phase === 'post' ? 'bg-violet-50 text-violet-700' : 'bg-sky-50 text-sky-700'
					)}
				>
					{capability.phase === 'post' ? 'after the reply' : 'before the workflow'}
				</Badge>
				<code className='ml-auto text-[11px] text-gray-700'>{capability.id}</code>
			</div>

			{capability.description && (
				<p className='mt-1.5 text-[12px] leading-4 text-gray-900'>{capability.description}</p>
			)}

			{capability.keywords.length > 0 && (
				<p className='mt-1.5 text-[11px] text-gray-800'>
					Spotted when the SOP says: {capability.keywords.map((keyword) => `“${keyword}”`).join(', ')}
				</p>
			)}

			{capability.params && capability.params.length > 0 && (
				<ul className='mt-1.5 space-y-0.5'>
					{capability.params.map((param) => (
						<li key={param.name} className='text-[11px] text-gray-800'>
							Asks: {param.label}
							{param.options?.length ? ` (${param.options.join(' / ')})` : ''}
							{!param.required && ' — optional'}
						</li>
					))}
				</ul>
			)}
		</li>
	);
}

function IssueList({ issues }: { issues: CapabilityDocIssue[] }) {
	const errors = errorsOf(issues);
	const warnings = warningsOf(issues);
	if (errors.length === 0 && warnings.length === 0) return null;

	return (
		<div className='space-y-2'>
			{errors.length > 0 && (
				<div className='rounded-xl border border-red-200 bg-red-50 p-3'>
					<p className='flex items-center gap-1.5 text-[12px] font-medium text-red-800'>
						<AlertTriangle className='size-3.5' />
						{errors.length === 1 ? 'One problem to fix' : `${errors.length} problems to fix`}
					</p>
					<ul className='mt-1.5 space-y-1'>
						{errors.map((issue) => (
							<li key={`${issue.line}-${issue.message}`} className='text-[12px] text-red-700'>
								<span className='tabular-nums opacity-70'>Line {issue.line}:</span> {issue.message}
							</li>
						))}
					</ul>
				</div>
			)}
			{warnings.length > 0 && (
				<div className='rounded-xl border border-amber-200 bg-amber-50 p-3'>
					<p className='flex items-center gap-1.5 text-[12px] font-medium text-amber-800'>
						<TriangleAlert className='size-3.5' />
						Worth a look
					</p>
					<ul className='mt-1.5 space-y-1'>
						{warnings.map((issue) => (
							<li key={`${issue.line}-${issue.message}`} className='text-[12px] text-amber-700'>
								<span className='tabular-nums opacity-70'>Line {issue.line}:</span> {issue.message}
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);
}

export default function TicketProcessingPage() {
	const { can } = usePermissions();
	const canEdit = can('edit', 'sop.approve');
	// This page holds the document in local state rather than a query atom, so the brand
	// has to be a dependency of the loader: switching brands must reload the document the
	// way a keyed query would, or the editor keeps showing the previous brand's text and
	// the next save writes it to the new one.
	const { buId } = useBusinessUnit();
	const scope = useMemo(() => (buId ? { bu_id: buId } : {}), [buId]);

	const [doc, setDoc] = useState<CapabilityDoc | null>(null);
	const [content, setContent] = useState('');
	const [capabilities, setCapabilities] = useState<ProcessingCapability[]>([]);
	const [issues, setIssues] = useState<CapabilityDocIssue[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [isParsing, setIsParsing] = useState(false);

	/** The saved text, so "unsaved changes" is a fact rather than a guess. */
	const savedContentRef = useRef('');

	const load = useCallback(async () => {
		setIsLoading(true);
		try {
			const loaded = await processingCapabilityService.get(scope);
			setDoc(loaded);
			setContent(loaded.content);
			savedContentRef.current = loaded.content;
			setCapabilities(loaded.capabilities);
			setIssues(loaded.issues);
		} catch {
			toast.error('Could not load the capability document.');
		} finally {
			setIsLoading(false);
		}
	}, [scope]);

	useEffect(() => {
		void load();
	}, [load]);

	// Parse as they type, so "did that capability register?" is answered without a save.
	useEffect(() => {
		if (isLoading) return;
		if (content === savedContentRef.current && doc) {
			setCapabilities(doc.capabilities);
			setIssues(doc.issues);
			return;
		}

		let cancelled = false;
		setIsParsing(true);
		const timer = setTimeout(async () => {
			try {
				const parsed = await processingCapabilityService.preview(content);
				if (cancelled) return;
				setCapabilities(parsed.capabilities);
				setIssues(parsed.issues);
			} catch {
				// A failed preview is not worth a toast on every keystroke; the save still validates.
			} finally {
				if (!cancelled) setIsParsing(false);
			}
		}, PREVIEW_DEBOUNCE_MS);

		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [content, doc, isLoading]);

	const isDirty = content !== savedContentRef.current;
	const blockingErrors = useMemo(() => errorsOf(issues), [issues]);

	const handleSave = async () => {
		setIsSaving(true);
		try {
			const saved = await processingCapabilityService.save(
				{
					content,
					...(doc ? { expectedVersion: doc.version } : {})
				},
				scope
			);
			setDoc(saved);
			savedContentRef.current = saved.content;
			setCapabilities(saved.capabilities);
			setIssues(saved.issues);
			toast.success(
				saved.capabilities.length === 1
					? '1 capability saved. The next generation will use it.'
					: `${saved.capabilities.length} capabilities saved. The next generation will use them.`
			);
		} catch (error: unknown) {
			if (error instanceof CapabilityDocInvalid) {
				setIssues(error.issues);
				toast.error('Not saved — fix the problems listed on the right first.');
			} else if (error instanceof CapabilityDocConflict) {
				toast.error('Someone else saved this document. Reload before saving your changes.');
			} else {
				toast.error('Could not save the document.');
			}
		} finally {
			setIsSaving(false);
		}
	};

	const handleReset = async () => {
		try {
			const defaults = await processingCapabilityService.defaults();
			setContent(defaults.content);
			toast.success('Loaded the built-in capabilities. Save to apply them.');
		} catch {
			toast.error('Could not load the built-in capabilities.');
		}
	};

	return (
		<div className='flex w-full flex-col'>
			<div className='sticky top-0 z-10 flex h-[60px] shrink-0 items-center gap-3 border-b border-gray-500 bg-white/80 px-4 backdrop-blur-[6px]'>
				<p className='text-base font-semibold leading-6 text-ink'>Ticket Processing</p>
				{doc && !doc.edited && (
					// Say plainly that this is the shipped list, not something someone here wrote.
					<Badge variant='outline' className='rounded-full border-0 bg-gray-100 text-[10px] text-gray-600'>
						Built-in defaults
					</Badge>
				)}
				{isDirty && (
					<Badge variant='outline' className='rounded-full border-0 bg-amber-50 text-[10px] text-amber-700'>
						Unsaved changes
					</Badge>
				)}

				<div className='ml-auto flex items-center gap-2'>
					{canEdit && (
						<Button variant='outline' size='sm' className='h-8 text-[12px]' onClick={() => void handleReset()}>
							<RotateCcw className='size-3.5' />
							Load defaults
						</Button>
					)}
					{canEdit && (
						<Button
							size='sm'
							className='h-8 text-[12px]'
							onClick={() => void handleSave()}
							// A document with errors cannot be saved: a capability that stops being
							// detected fails silently, and the first sign is a SOP step quietly
							// becoming a workflow step.
							disabled={isSaving || !isDirty || blockingErrors.length > 0}
						>
							{isSaving ? <Loader2 className='size-3.5 animate-spin' /> : <Save className='size-3.5' />}
							Save
						</Button>
					)}
				</div>
			</div>

			<div className='flex flex-col gap-4 p-4'>
				<div className='flex items-start gap-2 rounded-xl border border-gray-300 bg-gray-50/70 p-3'>
					<Info className='mt-0.5 size-4 shrink-0 text-gray-700' />
					<p className='text-[12px] leading-4 text-gray-900'>
						What Kim can do to a ticket in code, before and after a workflow runs. When a SOP describes one of
						these steps, generation offers to handle it here instead of building it into the workflow — which
						keeps the workflow smaller and its behaviour fixed. Changes apply to the next generation; existing
						workflows are untouched.
						{doc?.updatedAt && (
							<>
								{' '}
								Last saved {new Date(doc.updatedAt).toLocaleString()}
								{doc.updatedBy ? ` by user ${doc.updatedBy}` : ''}.
							</>
						)}
					</p>
				</div>

				{isLoading ? (
					<div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
						<Skeleton className='h-[60vh] w-full rounded-2xl' />
						<Skeleton className='h-[60vh] w-full rounded-2xl' />
					</div>
				) : (
					<div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
						{/* The document. */}
						<section className='flex min-w-0 flex-col gap-2'>
							<div className='flex items-center gap-2'>
								<h2 className='text-[13px] font-medium text-ink'>The document</h2>
								{isParsing && <Loader2 className='size-3 animate-spin text-gray-700' />}
							</div>
							<textarea
								value={content}
								onChange={(event) => setContent(event.target.value)}
								readOnly={!canEdit}
								spellCheck={false}
								className={cn(
									'h-[60vh] w-full resize-none rounded-2xl border border-gray-300 p-3 font-mono text-[12px] leading-5 text-ink',
									'focus:border-violet-300 focus:outline-none focus:ring-2 focus:ring-violet-100',
									!canEdit && 'bg-gray-50 text-gray-900'
								)}
							/>
							<p className='text-[11px] text-gray-800'>
								Each capability is a <code>###</code> heading under{' '}
								<code>## Pre-processing</code> or <code>## Post-processing</code>, with an{' '}
								<code>id</code>, a <code>what it does</code> line, the phrases a SOP might use, and any
								values it <code>needs</code>. Everything else is yours to write.
							</p>
						</section>

						{/* What the pipeline will actually see. */}
						<section className='flex min-w-0 flex-col gap-2'>
							<div className='flex items-center gap-2'>
								<h2 className='text-[13px] font-medium text-ink'>What generation will use</h2>
								{blockingErrors.length === 0 && capabilities.length > 0 && (
									<span className='flex items-center gap-1 text-[11px] text-emerald-700'>
										<Check className='size-3' />
										{capabilities.length} capabilit{capabilities.length === 1 ? 'y' : 'ies'}
									</span>
								)}
							</div>

							<div className='flex h-[60vh] flex-col gap-3 overflow-y-auto rounded-2xl border border-gray-300 p-3'>
								<IssueList issues={issues} />

								{capabilities.length === 0 ? (
									<p className='text-[12px] text-gray-800'>
										Nothing here yet. Without any capabilities, generation turns every pre/post step a
										SOP describes into a workflow step.
									</p>
								) : (
									<ul className='space-y-2'>
										{capabilities.map((capability) => (
											<CapabilityCard key={capability.id} capability={capability} />
										))}
									</ul>
								)}
							</div>
						</section>
					</div>
				)}
			</div>
		</div>
	);
}
